import { isUndefined } from '@aloft/toolkit-shared';
import { Flex, Icon, IconButton, Modal, Tooltip, Typography } from '@aloft/toolkit-sui';

import { useState } from 'react';
import { FieldPath, useFormContext } from 'react-hook-form';

import { selectCompSequence } from '../../../redux/features/selectors/orderSelectors.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import { PropertyGridFieldName, PropertyGridForm } from '../types.ts';
import { handleUpdatePropertyData } from '../utils.ts';
import { readOnlyFieldTypographyProps } from './consts.ts';
import { PropertyGridFieldProps } from './types.ts';

const AutofillSubjectValueButton = ({ fieldName }: { fieldName: PropertyGridFieldName }) => {
  const dispatch = useAppDispatch();
  const compSequence = useAppSelector(selectCompSequence);

  const { watch, setValue } = useFormContext<PropertyGridForm>();

  const [isConfirmationModalOpen, setConfirmationModalOpen] = useState(false);

  const subjectValue = watch(`columns.0.propertyData.${fieldName}` as FieldPath<PropertyGridForm>);

  const columns = watch('columns');

  const compFields = compSequence.map((compId) => ({
    compId,
    columnIndex: columns.findIndex(
      ({ propertyId, type }) => propertyId === compId && type === 'property',
    ),
  }));

  const allCompsMatchSubject = compFields.every(
    ({ columnIndex }) =>
      watch(`columns.${columnIndex}.propertyData.${fieldName}` as FieldPath<PropertyGridForm>) ===
      subjectValue,
  );

  const canAutoFill = compFields.every(({ columnIndex }) => {
    const compValue = watch(
      `columns.${columnIndex}.propertyData.${fieldName}` as FieldPath<PropertyGridForm>,
    );

    return isUndefined(compValue) || compValue === subjectValue;
  });

  const handleAutofill = () => {
    compSequence.forEach((compId) => {
      const compIndex = columns.findIndex(
        ({ propertyId, type }) => propertyId === compId && type === 'property',
      );

      const propertyData = watch(`columns.${compIndex}.propertyData`);

      setValue(`columns.${compIndex}.propertyData.${fieldName}`, subjectValue as any);
      if (propertyData) handleUpdatePropertyData(propertyData, compId, watch(), dispatch);
    });
  };

  const tooltipLabel = 'Paste Subj. Value Across Comps';
  const isDisabled = isUndefined(subjectValue) || allCompsMatchSubject;

  return (
    <>
      <Modal
        size="lg"
        isOpen={isConfirmationModalOpen}
        onClose={() => setConfirmationModalOpen(false)}
        closeOnEsc
        title="Existing Feature Values"
        primaryAction={{
          id: 'override-existing-values-btn',
          text: 'Override',
          onClick: () => {
            handleAutofill();
            setConfirmationModalOpen(false);
          },
        }}
        secondaryAction={{
          text: 'Cancel',
          onClick: () => setConfirmationModalOpen(false),
        }}
      >
        Some comparables already have a value set for this feature. Do you want to overwrite the
        existing values?
      </Modal>
      <Tooltip label={tooltipLabel}>
        <Flex
          paddingRight={isDisabled ? '8px' : '4px'}
          cursor={isDisabled ? 'not-allowed' : 'pointer'}
        >
          {isDisabled && <Icon icon="autofill" color="strokeDefault" />}
          {!isDisabled && (
            <IconButton
              variant="tertiary"
              ariaLabel={tooltipLabel}
              icon="autofill"
              iconColor="textPrimary"
              size="xs"
              onClick={() => {
                if (canAutoFill) handleAutofill();
                else setConfirmationModalOpen(true);
              }}
            />
          )}
        </Flex>
      </Tooltip>
    </>
  );
};

export const LabelField = <T, U extends string | number>({
  columnIndex,
  field: { fieldName, label, autofillSubjectValue },
}: PropertyGridFieldProps<T, U>) => {
  if (autofillSubjectValue && columnIndex === 0) {
    return (
      <Flex backgroundColor="surface2" height="32px" alignItems="center">
        <Typography {...readOnlyFieldTypographyProps}>{label}</Typography>
        <AutofillSubjectValueButton fieldName={fieldName} />
      </Flex>
    );
  }

  return <Typography {...readOnlyFieldTypographyProps}>{label}</Typography>;
};
